/**
 * Catalogo por ejes (snippets/cl-theme-catalog.liquid): Explorar y pagina
 * de coleccion.
 *
 * Coleccion base (config.baseCollection): en la pagina de coleccion todo se
 * filtra dentro de ella. Colecciones padre (config.children, del metacampo
 * custom.hijas): sin productos propios, muestran la union de sus hijas y
 * las hijas salen como subfiltro.
 *
 * Extraído del <script> inline de la sección para que el navegador lo pueda
 * cachear entre cargas de página. La configuración que antes salía
 * directamente de los settings del bloque (Liquid) ahora llega por sección
 * vía un <script type="application/json" data-jm-eje-config> (ver el
 * snippet), leído acá como `config`. Soporta más de una instancia de la
 * sección en la misma página (aunque hoy solo se usa una, en /pages/explorar)
 * y se re-inicializa si el editor de temas reemplaza la sección por AJAX.
 */
(() => {
  const initEje = (section) => {
    if (!section || section.dataset.jmEjeBound === 'true') return;
    section.dataset.jmEjeBound = 'true';

    let config = {};
    try {
      const configEl = section.querySelector('[data-jm-eje-config]');
      config = configEl ? JSON.parse(configEl.textContent || '{}') : {};
    } catch (error) {
      config = {};
    }


      const normalizeCache = new Map();
      const normalize = (value) => {
        const raw = String(value || '');
        if (normalizeCache.has(raw)) return normalizeCache.get(raw);
        const normalized = raw
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim();
        normalizeCache.set(raw, normalized);
        return normalized;
      };

      const get = (selector) => section.querySelector(selector);
      const params = new URLSearchParams(window.location.search);
      const requestedAxis = params.get('axis') || params.get('eje') || '';
      const requestedCollection = params.get('collection') || '';

      const titleEl = get('[data-jm-eje-title]');
      const axisNav = get('[data-jm-eje-axis-nav]');
      const layout = get('[data-jm-eje-layout]');
      const unavailable = get('[data-jm-eje-unavailable]');
      const collectionBox = get('[data-jm-eje-collections]');
      const collectionSearch = get('[data-jm-eje-collection-search]');
      const collectionCount = get('[data-jm-eje-collection-count]');
      const tagsBox = get('[data-jm-eje-tags]');
      const grid = get('[data-jm-eje-grid]');
      const empty = get('[data-jm-eje-empty]');
      const childrenBox = get('[data-jm-eje-children]');
      const loading = get('[data-jm-eje-loading]');
      const searchInput = get('[data-jm-eje-search]');
      const sortSelect = get('[data-jm-eje-sort]');
      const mobileQuery = window.matchMedia('(max-width: 740px)');
      // movil y tablet: lista de colecciones en cuadricula con flechas
      const compactQuery = window.matchMedia('(max-width: 1023px)');

      const axisNodes = Array.from(section.querySelectorAll('[data-jm-eje-axis]'));
      const axes = axisNodes.map((node) => {
        const collections = String(node.dataset.axisCollections || '').split('|').map((item) => item.trim()).filter(Boolean);
        const collectionTitles = String(node.dataset.axisCollectionTitles || '').split('|').map((item) => item.trim());
        return {
          handle: node.dataset.axisHandle || '',
          handleKey: normalize(node.dataset.axisHandle || ''),
          title: node.dataset.axisTitle || '',
          label: node.dataset.axisLabel || node.dataset.axisTitle || '',
          collections,
          collectionKeys: collections.map(normalize),
          collectionTitles,
          // Colecciones destacadas del eje: solo cambian de color, no de sitio
          featuredKeys: new Set((node.dataset.axisFeatured || '').split('|').filter(Boolean).map(normalize)),
          collectionItems: collections.map((handle, index) => ({
            handle,
            key: normalize(handle),
            title: collectionTitles[index] || handle,
            search: normalize(`${handle} ${collectionTitles[index] || handle}`)
          })),
          panelVars: node.dataset.panelVars || '',
          navVars: node.dataset.navVars || ''
        };
      });

      // Coleccion base (pagina de coleccion) y subcolecciones de cada padre
      // Busqueda: la base son los resultados (indice y tarjetas desde
      // /search?...&view=data|explorer), tratados como una coleccion mas.
      const SEARCH = '__search__';
      const searchTerms = config.baseSearch || '';
      const baseHandle = searchTerms ? SEARCH : (config.baseCollection || '');
      const baseKey = normalize(baseHandle);
      const childrenByParent = new Map();
      Object.keys(config.children || {}).forEach((parent) => {
        const kids = (config.children[parent] || []).map(([handle, title]) => ({ handle, key: normalize(handle), title: title || handle }));
        childrenByParent.set(normalize(parent), kids);
      });
      const childrenOf = (handle) => childrenByParent.get(normalize(handle)) || [];
      // hija elegida de cada padre (clave del padre -> handle de la hija)
      const selectedChild = {};
      const effectiveHandle = (handle) => selectedChild[normalize(handle)] || handle;

      // En la pagina de coleccion, el eje al que pertenece la propia coleccion
      // sobra: ya esta elegida.
      // Busqueda de una coleccion de eje ("pocoyo" = licencia Pocoyo): ese eje
      // sobra, igual que en la pagina de la coleccion.
      const searchKey = normalize(searchTerms).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const searchOwnsAxis = (axis) => Boolean(searchKey) && axis.collectionItems.some((item) => item.key === searchKey
        || normalize(item.title).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') === searchKey);

      const dropBaseAxis = () => {
        if (searchTerms) {
          for (let i = axes.length - 1; i >= 0; i -= 1) {
            if (searchOwnsAxis(axes[i])) axes.splice(i, 1);
          }
          return;
        }
        if (!baseKey) return;
        for (let i = axes.length - 1; i >= 0; i -= 1) {
          // la coleccion es del eje, o hija de un padre del eje
          const owns = axes[i].collectionKeys.includes(baseKey)
            || axes[i].collectionKeys.some((key) => childrenOf(key).some((kid) => kid.key === baseKey));
          if (owns) axes.splice(i, 1);
        }
      };
      dropBaseAxis();

      let activeAxis = null;
      let activeCollection = '';
      const selectedByAxis = {};
      let baseAxisHandle = '';
      // "Ver todos" de un eje: se llegó con ?axis= sin ?collection= (o con una
      // colección que no pertenece a ese eje). No se autoselecciona ninguna
      // colección — el catálogo se arma con la unión de todas las del eje.
      let axisAllMode = false;

      const htmlCache = new Map();
      const productIndexCache = new Map();

      // 'classic': cada eje muestra todas sus colecciones.
      // 'smart': solo las que siguen teniendo productos con lo ya elegido.
      const smartMode = config.filterMode === 'smart';
      const soldOutLast = config.soldOutLast !== false;
      // Textos de la interfaz: vienen traducidos del tema (locales, cl_catalog)
      const L = Object.assign({
        all: 'Todas', clearFilters: 'Limpiar filtros', noSelection: 'Sin selección',
        noResults: 'No hay productos que coincidan con los filtros seleccionados.',
        axisDisabled: 'Sin colecciones con los filtros elegidos',
        noCollectionsMatch: 'Ninguna colección de este eje encaja con los filtros elegidos.',
        noCollections: 'No hay colecciones.', loadError: 'No se pudieron cargar los productos. Recarga la página.',
        sortAz: 'Orden A a Z', sortZa: 'Orden Z a A'
      }, config.labels || {});
      const noResultsMessage = L.noResults;
      const fetchTimeoutMs = 4500;

      const sortMap = {
        manual: 'manual',
        'title-asc': 'title-ascending',
        'title-desc': 'title-descending',
        'price-asc': 'price-ascending',
        'price-desc': 'price-descending'
      };

      const findCollectionForAxis = (axis, handle) => {
        if (!axis || !handle) return '';
        const key = normalize(handle);
        const index = axis.collectionKeys.findIndex((collectionKey) => collectionKey === key);
        return index >= 0 ? axis.collections[index] : '';
      };

      if (requestedAxis) {
        const requestedAxisKey = normalize(requestedAxis);
        activeAxis = axes.find((axis) => axis.handleKey === requestedAxisKey);
      }

      if (!activeAxis && requestedCollection) {
        const requestedCollectionKey = normalize(requestedCollection);
        activeAxis = axes.find((axis) => axis.collectionKeys.includes(requestedCollectionKey));
      }

      // Si la URL ya resolvió un eje (por ?axis= o por un ?collection= que
      // pertenece a ese eje), respetamos "sin colección" en vez de forzar la
      // primera. El fallback a axes[0] de más abajo (URL totalmente vacía)
      // sigue preseleccionando eje + colección como antes.
      // ?collection= de una hija: se elige su padre en el eje y la hija en el
      // subfiltro (p. ej. Futbol -> Deportes > Futbol)
      let requestedChild = '';
      if (requestedCollection && !axes.some((axis) => axis.collectionKeys.includes(normalize(requestedCollection)))) {
        const childKey = normalize(requestedCollection);
        for (const axis of axes) {
          const parent = axis.collections.find((handle) => childrenOf(handle).some((kid) => kid.key === childKey));
          if (parent) {
            activeAxis = axis;
            requestedChild = parent;
            selectedChild[normalize(parent)] = requestedCollection;
            break;
          }
        }
      }

      if (activeAxis) {
        activeCollection = findCollectionForAxis(activeAxis, requestedChild || requestedCollection);
        if (activeCollection) {
          selectedByAxis[activeAxis.handle] = activeCollection;
          baseAxisHandle = activeAxis.handle;
        } else {
          axisAllMode = true;
          baseAxisHandle = activeAxis.handle;
        }
      }

      if (!activeAxis && axes.length && baseKey) {
        activeAxis = axes[0];
      } else if (!activeAxis && axes.length) {
        activeAxis = axes[0];
        activeCollection = activeAxis.collections[0] || '';
        if (activeCollection) {
          selectedByAxis[activeAxis.handle] = activeCollection;
          baseAxisHandle = activeAxis.handle;
        }
      }

      if (!activeAxis) {
        if (layout) layout.hidden = true;
        if (unavailable) unavailable.hidden = false;
        return;
      }

      if (unavailable) unavailable.hidden = true;
      if (layout) layout.hidden = false;

      const selectedAxisList = () => axes.filter((axis) => selectedByAxis[axis.handle]);

      const syncActiveCollection = () => {
        activeCollection = activeAxis ? selectedByAxis[activeAxis.handle] || '' : '';
      };

      const ensureBaseAxis = () => {
        const currentBase = axes.find((axis) => axis.handle === baseAxisHandle);
        if (currentBase && selectedByAxis[currentBase.handle]) return currentBase;

        const nextBase = activeAxis && selectedByAxis[activeAxis.handle]
          ? activeAxis
          : selectedAxisList()[0];

        baseAxisHandle = nextBase ? nextBase.handle : '';
        return nextBase || null;
      };

      // ----------------------------------------------------------------
      // Modo inteligente: que colecciones de cada eje siguen teniendo
      // productos con lo ya elegido.
      //
      // Cada producto lleva la etiqueta "{Eje} - {Valor}" de cada coleccion de
      // eje a la que pertenece (las colecciones son smart collections sobre
      // esa etiqueta). Asi, con los productos que quedan tras filtrar, se sabe
      // que colecciones siguen vivas sin pedir nada mas a la red. El prefijo
      // de cada eje se aprende de los propios productos: los de la coleccion X
      // del eje A llevan "{prefijo} - X". Las comparaciones van por handle, no
      // por texto visible, para que no dependa del idioma.
      //
      // Para un eje se miran los productos que cumplen los filtros de los
      // DEMAS ejes (faceta clasica): asi el eje con seleccion sigue ofreciendo
      // sus alternativas en vez de quedarse solo con la elegida.
      // ----------------------------------------------------------------
      const axisByPrefix = new Map();
      let availableByAxis = {};
      let lastAxisAllItems = null;

      const handleizeValue = (value) => normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const tagParts = (tag) => {
        const index = String(tag || '').indexOf(' - ');
        if (index < 0) return null;
        return { prefix: handleizeValue(tag.slice(0, index)), value: handleizeValue(tag.slice(index + 3)) };
      };
      // "dragon-ball-1" es la coleccion de la etiqueta "Licencia - Dragon Ball"
      const baseHandleKey = (key) => key.replace(/-\d+$/, '');

      const learnAxisPrefix = (axis, handle, items) => {
        if (!axis || !items.length) return;
        const key = normalize(handle);
        const baseKey = baseHandleKey(key);
        for (let i = 0; i < Math.min(items.length, 10); i += 1) {
          const found = items[i].tags.map(tagParts).find((parts) => parts && (parts.value === key || parts.value === baseKey));
          if (found) {
            axisByPrefix.set(found.prefix, axis.handle);
            return;
          }
        }
      };

      const axisForCollection = (handle) => {
        const key = normalize(handle);
        return axes.find((axis) => axis.collectionKeys.includes(key)) || null;
      };

      const collectionKeysFromItems = (items, axis) => {
        const valid = new Set();
        const byKey = new Map();
        axis.collectionKeys.forEach((key) => {
          byKey.set(key, key);
          if (!byKey.has(baseHandleKey(key))) byKey.set(baseHandleKey(key), key);
          // la etiqueta de una hija hace "viva" a su padre
          childrenOf(key).forEach((kid) => { if (!byKey.has(kid.key)) byKey.set(kid.key, key); });
        });
        items.forEach((item) => {
          item.tags.forEach((tag) => {
            const parts = tagParts(tag);
            if (!parts || axisByPrefix.get(parts.prefix) !== axis.handle) return;
            const key = byKey.get(parts.value);
            if (key) valid.add(key);
          });
        });
        return valid;
      };

      const cachedIndex = async (handle) => {
        try {
          return { handle, items: await fetchCollectionProductIndex(handle) };
        } catch (error) {
          return null;
        }
      };

      const computeAvailability = async (axisAllItems) => {
        if (!smartMode) {
          availableByAxis = {};
          return;
        }
        const selections = selectedRouteSelections();
        const learnedAxes = new Set(axisByPrefix.values());
        const next = {};
        for (const axis of axes) {
          // Sin el prefijo de este eje no se puede deducir nada: se muestran
          // todas sus colecciones.
          if (!learnedAxes.has(axis.handle)) continue;

          const others = selections.filter((selection) => !selection.axis || selection.axis.handle !== axis.handle);
          let items = null;
          if (others.length) {
            const indexes = (await Promise.all(others.map((selection) => cachedIndex(selection.handle)))).filter(Boolean);
            if (indexes.length !== others.length) continue;
            items = intersectIndexes(indexes);
          } else if (!baseKey && axisAllMode && activeAxis && axis.handle !== activeAxis.handle && axisAllItems) {
            items = axisAllItems;
          }
          if (items) next[axis.handle] = collectionKeysFromItems(items, axis);
        }
        availableByAxis = next;
      };

      const collectionLabel = (axis, handle) => {
        const selectedKey = normalize(handle);
        const index = axis.collectionKeys.findIndex((key) => key === selectedKey);
        return axis.collectionTitles[index] || handle;
      };


      const setLoading = (isLoading) => {
        if (loading) loading.hidden = !isLoading;
        if (grid) grid.classList.toggle('is-loading', isLoading);
      };

      const clearProducts = () => {
        if (grid) grid.replaceChildren();
        if (empty) {
          empty.hidden = true;
          empty.textContent = '';
        }
      };

      const showEmpty = (message = noResultsMessage) => {
        if (grid) grid.replaceChildren();
        if (empty) {
          empty.hidden = false;
          empty.textContent = message;
        }
      };

      // Vuelca en el elemento las variables que cl-theme-palette-vars genero
      // para una paleta. Las que esa paleta no trae quedan en "initial" para
      // que el CSS use su respaldo (la paleta de la seccion o el tema).
      const paletteKeys = ['bg', 'card-bg', 'text', 'text-hover', 'text-2', 'text-2-hover', 'button', 'button-hover', 'button-solid', 'button-hover-solid'];
      const applyPaletteVars = (el, prefix, vars) => {
        paletteKeys.forEach((key) => el.style.setProperty(`--${prefix}-${key}`, 'initial'));
        String(vars || '').split(';').forEach((declaration) => {
          const index = declaration.indexOf(':');
          if (index < 0) return;
          const name = declaration.slice(0, index).trim();
          const value = declaration.slice(index + 1).trim();
          if (name.startsWith(`--${prefix}-`) && value) el.style.setProperty(name, value);
        });
      };

      const setPalette = () => {
        applyPaletteVars(section, 'jm-eje-panel', activeAxis.panelVars);
        applyPaletteVars(section, 'jm-eje-nav', activeAxis.navVars);
      };

      const setHeader = () => {
        if (baseKey) return;
        const title = activeAxis.title || activeAxis.handle;
        if (titleEl) titleEl.textContent = title;
      };

      const updateExplorerUrl = () => {
        syncActiveCollection();
        try {
          const url = new URL(window.location.href);
          // en la pagina de coleccion la URL solo cambia si hay filtros
          if (baseKey && !activeCollection) url.searchParams.delete('axis');
          else if (activeAxis?.handle) url.searchParams.set('axis', activeAxis.handle);
          if (activeCollection) url.searchParams.set('collection', activeCollection);
          else url.searchParams.delete('collection');
          url.searchParams.delete('page');
          window.history.replaceState({}, '', url.toString());
        } catch (error) {
          /* noop */
        }
      };

      const buildButton = (className, text, onClick, options = {}) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        if (options.html) button.innerHTML = text;
        else if (options.title) window.clThemeTitle(button, text);
        else button.textContent = text;
        if (options.disabled) button.disabled = true;
        if (options.active) button.classList.add('is-active');
        button.addEventListener('click', onClick);
        return button;
      };

      const activateAxis = (axis) => {
        activeAxis = axis;
        syncActiveCollection();
        if (collectionSearch) collectionSearch.value = '';
        // En "todas" de un eje los productos dependen del eje activo: al
        // cambiar de pestana hay que recargarlos para que coincidan.
        renderAll(!baseKey && axisAllMode && !selectedAxisList().length);
      };

      // Todos los ejes salen siempre. Uno sin ninguna coleccion posible con lo
      // ya elegido (y sin seleccion propia) sale apagado y no se puede pulsar.
      const axisEnabled = (axis) => {
        if (!axis.collections.length) return false;
        const available = availableByAxis[axis.handle];
        return !available || available.size > 0 || Boolean(selectedByAxis[axis.handle]);
      };

      const renderAxisNav = () => {
        if (!axisNav) return;
        const fragment = document.createDocumentFragment();
        const enabled = axes.filter(axisEnabled);
        // Ningun eje posible: sin columna de colecciones, solo productos.
        section.classList.toggle('has-no-axes', !enabled.length);
        if (enabled.length && !enabled.includes(activeAxis)) {
          activeAxis = enabled[0];
          syncActiveCollection();
          setPalette();
        }
        axes.forEach((axis) => {
          const isEnabled = enabled.includes(axis);
          const button = buildButton('jm-main-eje__axis-button', axis.label || axis.title || axis.handle, () => {
            if (isEnabled) activateAxis(axis);
          }, { active: isEnabled && axis.handleKey === activeAxis.handleKey, disabled: !isEnabled });
          if (!isEnabled) button.title = L.axisDisabled;
          applyPaletteVars(button, 'jm-eje-nav', axis.navVars);
          fragment.appendChild(button);
        });
        axisNav.replaceChildren(fragment);
      };

      // Orden de la lista: el del eje (metaobjeto) mientras no se toque el
      // boton; cada clic pasa a A-Z, luego Z-A y vuelve al del eje.
      // Alfabetico con los numeros por su valor.
      const collator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
      const sortToggle = get('[data-jm-eje-collections-sort]');
      let sortDirection = 0;
      sortToggle?.addEventListener('click', () => {
        sortDirection = sortDirection === 0 ? 1 : sortDirection === 1 ? -1 : 0;
        sortToggle.textContent = sortDirection === -1 ? 'Z-A' : 'A-Z';
        sortToggle.setAttribute('aria-label', sortDirection === -1 ? L.sortZa : L.sortAz);
        sortToggle.classList.toggle('is-active', sortDirection !== 0);
        renderCollections();
      });

      const getFilteredCollections = () => {
        const query = normalize(collectionSearch?.value || '');
        const available = availableByAxis[activeAxis.handle];
        const selectedKey = normalize(selectedByAxis[activeAxis.handle] || '');
        const items = activeAxis.collectionItems.filter((item) => {
          if (query && !item.search.includes(query)) return false;
          if (available && !available.has(item.key) && item.key !== selectedKey) return false;
          return true;
        });
        return sortDirection ? items.sort((a, b) => sortDirection * collator.compare(a.title, b.title)) : items;
      };

      const selectCollection = (handle) => {
        axisAllMode = false;
        const wasBaseAxis = activeAxis.handle === baseAxisHandle;
        const previousSelection = selectedByAxis[activeAxis.handle] || '';
        if (previousSelection) delete selectedChild[normalize(previousSelection)];
        selectedByAxis[activeAxis.handle] = handle;
        syncActiveCollection();

        if (!baseAxisHandle || wasBaseAxis || !selectedByAxis[baseAxisHandle]) {
          baseAxisHandle = activeAxis.handle;
        }

        if (searchInput) searchInput.value = '';
        updateExplorerUrl();
        renderCollections();
        renderTags();
        renderChildren();
        loadCollectionProducts({ scroll: mobileQuery.matches });
      };

      // Flechas de la lista de colecciones: avanzan casi una "pantalla" de la
      // lista y se apagan al llegar arriba o abajo.
      const collectionArrows = Array.from(section.querySelectorAll('[data-jm-eje-collections-scroll]'));
      // En movil, el nombre que no cabe entero en media fila ocupa la fila
      // entera. Se mide el texto real (no el numero de letras) y se vuelve a
      // medir al cargar la fuente y al cambiar el ancho de la pantalla.
      const markWideCollections = () => {
        const buttons = collectionBox.querySelectorAll('.jm-main-eje__collection-button');
        buttons.forEach((button) => button.classList.remove('is-wide'));
        if (!compactQuery.matches) return;
        buttons.forEach((button) => {
          if (button.scrollWidth > button.clientWidth + 1) button.classList.add('is-wide');
        });
      };
      document.fonts?.ready.then(markWideCollections);
      let wideTimer;
      window.addEventListener('resize', () => {
        clearTimeout(wideTimer);
        wideTimer = setTimeout(markWideCollections, 150);
      });

      const updateCollectionArrows = () => {
        if (!collectionBox) return;
        const maxScroll = collectionBox.scrollHeight - collectionBox.clientHeight;
        collectionArrows.forEach((arrow) => {
          const direction = Number(arrow.dataset.jmEjeCollectionsScroll);
          arrow.disabled = maxScroll <= 1
            || (direction < 0 && collectionBox.scrollTop <= 0)
            || (direction > 0 && collectionBox.scrollTop >= maxScroll - 1);
        });
      };
      collectionArrows.forEach((arrow) => {
        arrow.addEventListener('click', () => {
          if (!collectionBox) return;
          const direction = Number(arrow.dataset.jmEjeCollectionsScroll);
          // de fila en fila: la altura visible es un numero exacto de filas
          // y el scroll-snap del CSS las deja siempre enteras
          const first = collectionBox.querySelector('.jm-main-eje__collection-button');
          const gap = parseFloat(getComputedStyle(collectionBox).rowGap) || 0;
          const rowHeight = first ? first.getBoundingClientRect().height + gap : collectionBox.clientHeight;
          const rows = Math.max(1, Math.floor((collectionBox.clientHeight + gap) / rowHeight));
          collectionBox.scrollBy({ top: direction * rows * rowHeight, behavior: 'smooth' });
        });
      });
      collectionBox?.addEventListener('scroll', updateCollectionArrows, { passive: true });

      const renderCollections = () => {
        if (!collectionBox) return;
        const filtered = getFilteredCollections();
        const activeCollectionKey = normalize(selectedByAxis[activeAxis.handle] || '');
        const fragment = document.createDocumentFragment();

        filtered.forEach((item) => {
          const button = buildButton('jm-main-eje__collection-button', item.title, () => {
            selectCollection(item.handle);
          }, { active: activeCollectionKey === item.key, title: true });
          button.dataset.collectionHandle = item.handle;
          if (activeAxis.featuredKeys.has(item.key)) button.classList.add('is-featured');
          fragment.appendChild(button);
        });

        // Modo inteligente: ninguna coleccion de este eje tiene productos con
        // lo ya elegido. Se dice en vez de dejar la lista en blanco.
        if (!filtered.length) {
          const note = document.createElement('p');
          note.className = 'jm-main-eje__collections-empty';
          note.textContent = availableByAxis[activeAxis.handle]
            ? L.noCollectionsMatch
            : L.noCollections;
          fragment.appendChild(note);
        }

        // otro eje u otra busqueda: la lista empieza arriba
        const listKey = `${activeAxis.handle}|${collectionSearch?.value || ''}`;
        const previousScroll = collectionBox.dataset.listKey === listKey ? collectionBox.scrollTop : 0;
        collectionBox.dataset.listKey = listKey;
        collectionBox.replaceChildren(fragment);
        if (collectionCount) collectionCount.textContent = `${filtered.length} / ${activeAxis.collections.length}`;
        markWideCollections();
        // La lista se queda donde estaba: solo se mueve lo justo si la
        // coleccion elegida no esta a la vista (antes saltaba arriba del todo).
        collectionBox.scrollTop = previousScroll;
        const activeButton = collectionBox.querySelector('.jm-main-eje__collection-button.is-active');
        if (activeButton) {
          const top = activeButton.offsetTop - collectionBox.offsetTop;
          const bottom = top + activeButton.offsetHeight;
          if (top < collectionBox.scrollTop) collectionBox.scrollTop = top;
          else if (bottom > collectionBox.scrollTop + collectionBox.clientHeight) collectionBox.scrollTop = bottom - collectionBox.clientHeight;
        }
        updateCollectionArrows();
      };

      // Al quitar filtros se vuelve al eje principal (el primero, Productos)
      const backToMainAxis = () => {
        const main = axes.find((axis) => axis.collections.length) || axes[0];
        if (!main || main === activeAxis) return;
        activeAxis = main;
        syncActiveCollection();
        setPalette();
        setHeader();
        renderAxisNav();
      };

      const removeAxisSelection = (axis) => {
        if (selectedByAxis[axis.handle]) delete selectedChild[normalize(selectedByAxis[axis.handle])];
        delete selectedByAxis[axis.handle];
        if (axis.handle === baseAxisHandle) baseAxisHandle = '';
        if (axis.handle === activeAxis.handle) syncActiveCollection();
        ensureBaseAxis();
        // Sin ningun filtro se muestran todas las del eje activo, en vez del
        // aviso de "no hay productos".
        if (!selectedAxisList().length && !baseKey) axisAllMode = true;
        backToMainAxis();
        updateExplorerUrl();
        renderCollections();
        renderTags();
        renderChildren();
        loadCollectionProducts();
      };

      const renderTags = () => {
        if (!tagsBox) return;
        const fragment = document.createDocumentFragment();
        axes.forEach((axis) => {
          const selected = selectedByAxis[axis.handle];
          const isAxisAll = !selected && axisAllMode && activeAxis && axis.handle === activeAxis.handle;
          const tag = document.createElement('span');
          tag.className = 'jm-main-eje__tag';
          tag.classList.toggle('is-empty', !selected && !isAxisAll);
          tag.classList.toggle('is-base', selected && axis.handle === baseAxisHandle);
          const strong = document.createElement('strong');
          strong.textContent = `${axis.title || axis.handle}:`;
          const label = document.createElement('span');

          if (selected) {
            window.clThemeTitle(label, collectionLabel(axis, selected));
            const close = buildButton('', '×', (event) => {
              event.stopPropagation();
              removeAxisSelection(axis);
            });
            close.setAttribute('aria-label', `Quitar filtro ${axis.title || axis.handle}`);
            tag.append(strong, ' ', label, close);
          } else if (isAxisAll) {
            label.textContent = L.all;
            tag.append(strong, ' ', label);
          } else {
            label.textContent = L.noSelection;
            tag.append(strong, ' ', label);
          }

          tag.addEventListener('click', () => {
            activateAxis(axis);
          });
          fragment.appendChild(tag);
        });

        if (selectedAxisList().length > 1) {
          fragment.appendChild(buildButton('jm-main-eje__clear-tags', L.clearFilters, () => {
            axes.forEach((axis) => {
              if (selectedByAxis[axis.handle]) delete selectedChild[normalize(selectedByAxis[axis.handle])];
              delete selectedByAxis[axis.handle];
            });
            baseAxisHandle = '';
            axisAllMode = !baseKey;
            syncActiveCollection();
            backToMainAxis();
            updateExplorerUrl();
            renderCollections();
            renderTags();
            renderChildren();
            loadCollectionProducts();
          }));
        }

        tagsBox.replaceChildren(fragment);
      };

      // ----------------------------------------------------------------
      // Subcolecciones: una fila por cada padre en juego (la base y las
      // elegidas en los ejes). "Todas" = union de las hijas. Solo salen las
      // hijas con productos dentro de lo ya filtrado (se mira con la hija sin
      // elegir y se recuerda mientras se elige una).
      // ----------------------------------------------------------------
      const childAvailability = new Map();

      const parentsInPlay = () => {
        const parents = [];
        if (baseKey && childrenOf(baseKey).length) parents.push({ handle: baseHandle, title: '' });
        selectedAxisList().forEach((axis) => {
          const handle = selectedByAxis[axis.handle];
          if (childrenOf(handle).length) parents.push({ handle, title: collectionLabel(axis, handle) });
        });
        return parents;
      };

      function renderChildren() {
        if (!childrenBox) return;
        const parents = parentsInPlay();
        const fragment = document.createDocumentFragment();
        parents.forEach((parent) => {
          const parentKey = normalize(parent.handle);
          const chosen = normalize(selectedChild[parentKey] || '');
          const available = childAvailability.get(parentKey);
          // hasta saber que hijas tienen productos con lo elegido no se pinta:
          // asi no se puede pulsar una que daria cero productos
          if (!available && !chosen) return;
          // ninguna hija con filtro propio y productos: la fila no aporta nada
          const shown = childrenOf(parentKey).filter((kid) => normalize(kid.title) !== 'otros' && (!available || available.has(kid.key)));
          if (!shown.length && !chosen) return;
          const row = document.createElement('div');
          row.className = 'jm-main-eje__children-row';
          if (parent.title) {
            const label = document.createElement('span');
            label.className = 'jm-main-eje__children-label';
            window.clThemeTitle(label, parent.title);
            row.appendChild(label);
          }
          const pick = (handle) => {
            if (handle) selectedChild[parentKey] = handle;
            else delete selectedChild[parentKey];
            renderChildren();
            loadCollectionProducts();
          };
          row.appendChild(buildButton('jm-main-eje__child', L.all, () => pick(''), { active: !chosen }));
          childrenOf(parentKey).forEach((kid) => {
            // "Otros" (sin motivo concreto) no sale como filtro: sus productos
            // estan en "Todas"
            if (normalize(kid.title) === 'otros') return;
            if (available && !available.has(kid.key) && kid.key !== chosen) return;
            row.appendChild(buildButton('jm-main-eje__child', kid.title, () => pick(kid.handle), { active: kid.key === chosen, title: true }));
          });
          fragment.appendChild(row);
        });
        childrenBox.replaceChildren(fragment);
        childrenBox.hidden = !fragment.childNodes.length && !childrenBox.children.length;
      }

      // Con los productos ya filtrados: que hijas de cada padre siguen vivas.
      // Cada producto del padre recuerda de que hija viene (sourceHandle).
      const updateChildAvailability = (products) => {
        parentsInPlay().forEach((parent) => {
          const parentKey = normalize(parent.handle);
          if (selectedChild[parentKey]) return;
          const kids = new Set(childrenOf(parentKey).map((kid) => kid.key));
          const alive = new Set();
          products.forEach((product) => {
            (product.childKeys || []).forEach((key) => { if (kids.has(key)) alive.add(key); });
          });
          childAvailability.set(parentKey, alive);
        });
      };

      const currentSortValue = () => sortMap[sortSelect?.value || 'manual'] || 'manual';
      const productsPerPage = () => Math.max(1, Number(grid?.dataset.perPage || 24));

      // ----------------------------------------------------------------
      // Origen de las tarjetas: vista ligera de colección.
      //
      // /collections/{handle}?view=explorer devuelve SOLO la rejilla de
      // productos (templates/collection.explorer.liquid, sin layout), con 50
      // tarjetas por página. La página de colección normal pesa ~386 KB de los
      // que apenas ~21 KB son la rejilla: el 94% era cabecera, menús y pie que
      // se descargaban, se parseaban con DOMParser y se tiraban.
      //
      // No se envía sort_by a propósito: el orden lo aplica
      // sortProductIndexItems sobre el índice en memoria, y las tarjetas se
      // localizan por handle, así que el orden de las páginas da igual. Omitirlo
      // además hace que la caché acierte más.
      // Raiz del idioma actual ("/" o "/en/"): las tarjetas se piden en el
      // idioma de la pagina, asi sus textos y enlaces no saltan al espanol
      const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      const searchUrl = (view, page) => {
        const url = new URL(`${ROOT}search`, window.location.origin);
        url.searchParams.set('q', searchTerms);
        url.searchParams.set('type', 'product');
        url.searchParams.set('view', view);
        if (page > 1) url.searchParams.set('page', String(page));
        return url;
      };

      const explorerCardsUrl = (handle, page = 1) => {
        if (handle === SEARCH) return searchUrl('explorer', page);
        const url = new URL(`${ROOT}collections/${encodeURIComponent(handle)}`, window.location.origin);
        url.searchParams.set('view', 'explorer');
        if (page > 1) url.searchParams.set('page', String(page));
        return url;
      };

      const fetchWithTimeout = async (url, options = {}) => {
        if (typeof AbortController === 'undefined') {
          return fetch(url, options);
        }

        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), fetchTimeoutMs);

        try {
          return await fetch(url, { ...options, signal: controller.signal });
        } finally {
          window.clearTimeout(timer);
        }
      };

      const pickProductList = (doc) => {
        return doc.querySelector('.jm-main-collection [data-jm-product-list]')
          || doc.querySelector('[data-jm-product-list]')
          || doc.querySelector('.product-list--collection')
          || doc.querySelector('.product-list--search')
          || doc.querySelector('[data-search-results] .product-list')
          || doc.querySelector('.product-list')
          || doc.querySelector('.product-grid')
          || doc.querySelector('#product-grid');
      };

      const productHandleFromCard = (card) => {
        const handleNode = card.matches('[data-product-handle], [data-handle]')
          ? card
          : card.querySelector('[data-product-handle], [data-handle]');
        const dataHandle = handleNode?.dataset?.productHandle || handleNode?.dataset?.handle || '';
        if (dataHandle) return dataHandle;

        const link = card.querySelector('a[href*="/products/"]');
        if (!link) return '';
        try {
          const url = new URL(link.getAttribute('href'), window.location.origin);
          const parts = url.pathname.split('/products/');
          return parts[1] ? decodeURIComponent(parts[1].split('/')[0].replace(/\.[a-z0-9]+$/i, '')) : '';
        } catch (error) {
          const match = String(link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
          return match ? decodeURIComponent(match[1]) : '';
        }
      };

      const numericKeysFromCard = (card) => {
        const keys = [];
        const add = (prefix, value) => {
          const raw = String(value || '').trim();
          if (/^\d+$/.test(raw)) {
            keys.push(`${prefix}:${raw}`);
            keys.push(`id:${raw}`);
          }
        };

        const dataNodes = [
          card,
          ...Array.from(card.querySelectorAll('[data-product-id], [data-productid], [data-product-id-value], [data-variant-id], [data-variantid]'))
        ];

        dataNodes.forEach((node) => {
          add('product', node.dataset.productId || node.dataset.productid || node.dataset.productIdValue);
          add('variant', node.dataset.variantId || node.dataset.variantid);
        });

        Array.from(card.querySelectorAll('[name="id"], [name="product_id"], [name="product-id"]')).forEach((node) => {
          add(node.getAttribute('name') === 'id' ? 'variant' : 'product', node.value);
        });

        return keys;
      };

      const fetchHtml = async (url) => {
        const cacheKey = url.toString();
        if (htmlCache.has(cacheKey)) return htmlCache.get(cacheKey);
        const promise = fetchWithTimeout(cacheKey, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .catch((error) => {
            htmlCache.delete(cacheKey);
            throw error;
          });
        htmlCache.set(cacheKey, promise);
        return promise;
      };
      const fetchJson = async (url) => {
        const response = await fetchWithTimeout(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      };

      const productJsonUrl = (handle, page = 1) => {
        const url = new URL(`/collections/${encodeURIComponent(handle)}/products.json`, window.location.origin);
        url.searchParams.set('limit', '250');
        if (page > 1) url.searchParams.set('page', String(page));
        return url;
      };

      const priceFromProduct = (product) => {
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const prices = variants
          .map((variant) => Number(variant?.price || variant?.compare_at_price || 0))
          .filter((price) => Number.isFinite(price) && price > 0);
        return prices.length ? Math.min(...prices) : 0;
      };

      const normalizeProductTags = (product) => {
        const rawTags = Array.isArray(product?.tags)
          ? product.tags
          : String(product?.tags || '').split(',');
        return rawTags.map((tag) => String(tag || '').trim()).filter(Boolean);
      };

      const productIndexItem = (product, order) => {
        const tags = normalizeProductTags(product);
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const variantText = variants.map((variant) => `${variant?.title || ''} ${variant?.sku || ''}`).join(' ');
        const handle = product?.handle || '';
        const title = product?.title || '';
        return {
          handle,
          handleKey: normalize(handle),
          title,
          titleKey: normalize(title),
          price: priceFromProduct(product),
          // sin variantes no se sabe: se trata como disponible
          available: !variants.length || variants.some((variant) => variant?.available !== false),
          order,
          tags,
          tagKeys: new Set(tags.map(normalize)),
          search: normalize(`${handle} ${title} ${product?.vendor || ''} ${product?.product_type || ''} ${tags.join(' ')} ${variantText}`)
        };
      };

      // 20 paginas x 250 = 5000 productos. La coleccion mas grande hoy
      // ('globo') son 1440, asi que hay 3x de holgura. El tope importa para la
      // correctitud: con la interseccion por colecciones, un indice truncado
      // devolveria de menos, por eso se avisa en consola si se alcanza.
      const MAX_INDEX_PAGES = 20;

      const fetchCollectionProductIndex = async (handle) => {
        const cacheKey = handle;
        if (productIndexCache.has(cacheKey)) return productIndexCache.get(cacheKey);

        if (handle === SEARCH) {
          const search = (async () => {
            const products = [];
            for (let page = 1, pages = 1; page <= pages && page <= MAX_INDEX_PAGES * 5; page += 1) {
              const data = await fetchJson(searchUrl('data', page));
              pages = Number(data?.pages) || 1;
              products.push(...(Array.isArray(data?.products) ? data.products : []));
            }
            const items = products.filter((product) => product?.handle).map((product, index) => productIndexItem(product, index));
            // La busqueda de Shopify es aproximada (con "elefantes" trae un
            // banderin de Dragon Ball): solo salen los productos que contienen
            // cada palabra buscada, o su singular ("elefantes" -> "elefante").
            // Si nada coincide, no sale nada: nunca productos sin relacion.
            const stem = (word) => {
              if (word.length > 4 && word.endsWith('es')) return word.slice(0, -2);
              if (word.length > 3 && word.endsWith('s')) return word.slice(0, -1);
              return word;
            };
            const words = normalize(searchTerms).split(/\s+/).filter((word) => word.length > 1);
            return items.filter((item) => words.every((word) => item.search.includes(word) || item.search.includes(stem(word))));
          })();
          productIndexCache.set(cacheKey, search);
          search.catch(() => productIndexCache.delete(cacheKey));
          return search;
        }

        // Padre: sus productos son los de sus hijas. Cada producto recuerda de
        // que hija viene (para pedir su tarjeta) y en que hijas esta.
        const kids = childrenOf(handle);
        if (kids.length) {
          const union = indexesFor(kids.map((kid) => kid.handle)).then((indexes) => {
            const keysByProduct = new Map();
            indexes.forEach(({ handle: kid, items }) => items.forEach((item) => {
              if (!keysByProduct.has(item.handleKey)) keysByProduct.set(item.handleKey, []);
              keysByProduct.get(item.handleKey).push(normalize(kid));
            }));
            return unionIndexes(indexes).map((item) => ({ ...item, childKeys: keysByProduct.get(item.handleKey) || [] }));
          });
          productIndexCache.set(cacheKey, union);
          union.catch(() => productIndexCache.delete(cacheKey));
          return union;
        }

        const promise = (async () => {
          const products = [];
          let page = 1;
          let keepGoing = true;

          while (keepGoing && page <= MAX_INDEX_PAGES) {
            const data = await fetchJson(productJsonUrl(handle, page));
            const batch = Array.isArray(data?.products) ? data.products : [];
            products.push(...batch);
            keepGoing = batch.length === 250;
            page += 1;
          }

          if (keepGoing) {
            console.warn(`[CL-EJE] indice truncado en ${handle}: mas de ${MAX_INDEX_PAGES * 250} productos. Sube MAX_INDEX_PAGES.`);
          }

          const items = products
            .filter((product) => product?.handle)
            .map((product, index) => productIndexItem(product, index));
          learnAxisPrefix(axisForCollection(handle), handle, items);
          return items;
        })().catch((error) => {
          productIndexCache.delete(cacheKey);
          throw error;
        });

        productIndexCache.set(cacheKey, promise);
        return promise;
      };

      const sortProductIndexItems = (items) => {
        const value = sortSelect?.value || 'manual';
        const sorted = [...items];
        if (value === 'title-asc') sorted.sort((a, b) => a.titleKey.localeCompare(b.titleKey) || a.order - b.order);
        else if (value === 'title-desc') sorted.sort((a, b) => b.titleKey.localeCompare(a.titleKey) || a.order - b.order);
        else if (value === 'price-asc') sorted.sort((a, b) => (a.price || 0) - (b.price || 0) || a.order - b.order);
        else if (value === 'price-desc') sorted.sort((a, b) => (b.price || 0) - (a.price || 0) || a.order - b.order);
        // Sin stock al final, sea cual sea el orden elegido (ajuste de la
        // seccion). Reparto estable: cada grupo conserva su orden.
        if (!soldOutLast) return sorted;
        return [...sorted.filter((item) => item.available), ...sorted.filter((item) => !item.available)];
      };

      const productItemFromNode = (node, byHandle) => {
        if (!(node instanceof Element) || node.matches('script, style')) return null;
        const card = node.cloneNode(true);
        const handle = productHandleFromCard(card);
        const handleKey = normalize(handle);
        if (!handleKey || byHandle.has(handleKey)) return null;

        return {
          handle,
          handleKey,
          keys: [`handle:${handleKey}`, ...numericKeysFromCard(card)],
          card,
          search: normalize(card.textContent)
        };
      };

      const debugLog = (...args) => console.debug('[CL-EJE]', ...args);

      const extractProductsFromDoc = (doc) => {
        const productList = pickProductList(doc);
        const items = [];
        const byHandle = new Map();

        if (productList) {
          Array.from(productList.children).forEach((node) => {
            const item = productItemFromNode(node, byHandle);
            if (!item) return;
            byHandle.set(item.handleKey, item);
            items.push(item);
          });
        }

        return { items, byHandle };
      };

      // Lo que filtra: la coleccion base (si hay) y la elegida en cada eje,
      // cambiadas por su hija si se ha elegido una.
      const selectedRouteSelections = () => {
        const selections = axes
          .map((axis) => ({ axis, handle: effectiveHandle(selectedByAxis[axis.handle] || '') }))
          .filter((selection) => selection.handle);
        if (baseHandle) selections.unshift({ axis: null, handle: effectiveHandle(baseHandle) });
        return selections;
      };

      // Lanza las peticiones en tandas pequeñas en vez de todas a la vez:
      // un eje puede tener decenas de colecciones y un estallido de
      // peticiones simultáneas ahoga al navegador y al storefront.
      const mapWithConcurrency = async (items, limit, mapper) => {
        const results = new Array(items.length);
        let index = 0;
        const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
          while (index < items.length) {
            const current = index++;
            results[current] = await mapper(items[current], current);
          }
        });
        await Promise.all(workers);
        return results;
      };


      // ----------------------------------------------------------------
      // Caché de tarjetas por colección.
      //
      // Cada colección se recorre una sola vez, en páginas de 50, y todas las
      // tarjetas vistas quedan memorizadas por handle. Así el scroll infinito
      // avanza secuencialmente sin volver a pedir lo mismo.
      // ----------------------------------------------------------------
      const MAX_CARD_PAGES = 40;
      const cardStore = new Map();

      const cardStoreFor = (handle) => {
        let entry = cardStore.get(handle);
        if (!entry) {
          entry = { cards: new Map(), nextPage: 1, totalPages: Infinity, exhausted: false };
          cardStore.set(handle, entry);
        }
        return entry;
      };

      const loadNextCardPage = async (handle) => {
        const entry = cardStoreFor(handle);
        if (entry.exhausted) return false;

        const page = entry.nextPage;
        const html = await fetchHtml(explorerCardsUrl(handle, page));
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const list = pickProductList(doc);
        const totalPages = Number(list?.dataset.totalPages || 0);
        if (totalPages > 0) entry.totalPages = totalPages;

        const { items } = extractProductsFromDoc(doc);
        items.forEach((item) => {
          if (!entry.cards.has(item.handleKey)) entry.cards.set(item.handleKey, item);
        });

        entry.nextPage = page + 1;
        if (!items.length || entry.nextPage > entry.totalPages) entry.exhausted = true;

        debugLog('cards page', {
          handle, page, received: items.length, cached: entry.cards.size, totalPages: entry.totalPages
        });
        return true;
      };

      const cardsForCollection = async (handle, handleKeys) => {
        const entry = cardStoreFor(handle);
        const pending = () => handleKeys.some((key) => !entry.cards.has(key));
        let guard = 0;

        while (pending() && !entry.exhausted && guard < MAX_CARD_PAGES) {
          guard += 1;
          if (!(await loadNextCardPage(handle))) break;
        }

        return entry.cards;
      };

      // Agrupa por colección de origen para no recorrer la misma colección dos
      // veces, y devuelve las tarjetas en el mismo orden que los productos.
      const cardsForProducts = async (products) => {
        const byCollection = new Map();
        products.forEach((product) => {
          const handle = product.sourceHandle;
          if (!handle) return;
          if (!byCollection.has(handle)) byCollection.set(handle, []);
          byCollection.get(handle).push(product.handleKey);
        });

        const stores = await Promise.all(
          Array.from(byCollection.entries()).map(([handle, keys]) => cardsForCollection(handle, keys))
        );

        const lookup = new Map();
        stores.forEach((cards) => {
          cards.forEach((item, key) => {
            if (!lookup.has(key)) lookup.set(key, item);
          });
        });

        return products.map((product) => lookup.get(product.handleKey)).filter(Boolean);
      };

      // ----------------------------------------------------------------
      // Qué productos coinciden con la selección.
      //
      // La pertenencia se resuelve con handles de colección y de producto, que
      // Shopify nunca traduce. Antes se reconstruía la etiqueta
      // "{Eje} - {Colección}" a partir de textos visibles (eje.nombre y el
      // título de la colección) y se filtraba por ella: en el dominio en inglés
      // eso generaba tag:"License - Barbie" contra productos etiquetados
      // "Licencia - Barbie", y el resultado era siempre cero. Además se rompía
      // en español con solo renombrar un eje o una colección.
      //
      // Las colecciones son smart collections construidas sobre esa etiqueta,
      // así que intersectarlas da el mismo resultado sin depender del idioma —
      // verificado contra el método anterior: mismos productos, mismos handles.
      // ----------------------------------------------------------------
      // Un fallo de red se reintenta una vez; si vuelve a fallar se propaga
      // para avisar, en vez de devolver una lista vacia que en una interseccion
      // dejaba la rejilla sin productos y sin explicacion.
      const indexesFor = (handles) => mapWithConcurrency(handles, 6, (handle) => fetchCollectionProductIndex(handle)
        .catch(() => fetchCollectionProductIndex(handle))
        .then((items) => ({ handle, items })));

      // Se parte de la colección más pequeña: es la que menos páginas de
      // tarjetas habrá que recorrer después.
      const intersectIndexes = (indexes) => {
        const ordered = [...indexes].sort((a, b) => a.items.length - b.items.length);
        const [base, ...others] = ordered;
        if (!base) return [];

        const sets = others.map((entry) => new Set(entry.items.map((item) => item.handleKey)));
        const childKeys = new Map();
        indexes.forEach((entry) => entry.items.forEach((item) => {
          if (item.childKeys) childKeys.set(item.handleKey, (childKeys.get(item.handleKey) || []).concat(item.childKeys));
        }));
        return base.items
          .filter((item) => sets.every((set) => set.has(item.handleKey)))
          .map((item) => ({ ...item, sourceHandle: item.sourceHandle || base.handle, childKeys: childKeys.get(item.handleKey) || item.childKeys }));
      };

      const unionIndexes = (indexes) => {
        const seen = new Set();
        const merged = [];
        indexes.forEach(({ handle, items }) => {
          items.forEach((item) => {
            if (seen.has(item.handleKey)) return;
            seen.add(item.handleKey);
            // un producto que ya viene de un padre conserva su hija: el padre
            // no tiene productos propios y su tarjeta no se encontraria
            merged.push({ ...item, sourceHandle: item.sourceHandle || handle });
          });
        });
        return merged;
      };

      const computeMatchingProducts = async () => {
        const query = normalize(searchInput?.value || '');
        const selections = selectedRouteSelections();
        let products = [];
        // Mientras se recalcula, la lista de colecciones aun es la del paso
        // anterior: se bloquea para no poder elegir una combinacion vacia.
        if (smartMode) section.classList.add('is-updating');

        if (selections.length) {
          products = intersectIndexes(await indexesFor(selections.map((selection) => selection.handle)));
        } else if (axisAllMode && activeAxis && activeAxis.collections.length) {
          // "Ver todos" de un eje: unión de todas sus colecciones.
          products = unionIndexes(await indexesFor(activeAxis.collections));
        }

        lastAxisAllItems = selections.length ? null : products;
        await computeAvailability(lastAxisAllItems);
        firstListDone();
        updateChildAvailability(products);
        renderAxisNav();
        renderCollections();
        renderTags();
        renderChildren();
        section.classList.remove('is-updating');

        if (query) products = products.filter((product) => product.search.includes(query));

        debugLog('computeMatchingProducts', {
          selections: selections.map((selection) => selection.handle),
          axisAll: axisAllMode ? activeAxis?.handle : null,
          matched: products.length,
          query
        });

        return sortProductIndexItems(products);
      };

      // ----------------------------------------------------------------
      // Scroll infinito.
      //
      // La lista completa ya está en memoria tras computeMatchingProducts, así
      // que avanzar no cuesta red por la lista: solo se piden las tarjetas del
      // lote que se va a mostrar.
      // ----------------------------------------------------------------
      const PROGRESSIVE_FIRST = 6;
      // primera lista calculada (para mostrar las pestanas ya con lo posible)
      let firstListDone;
      const firstList = new Promise((resolve) => { firstListDone = resolve; });
      const listState = { products: [], rendered: 0, token: 0, busy: false, done: true };
      let sentinel = null;

      const sentinelWithinReach = () => {
        if (!sentinel) return false;
        const rect = sentinel.getBoundingClientRect();
        return rect.top <= (window.innerHeight || document.documentElement.clientHeight || 0) + 600;
      };

      const ensureSentinel = () => {
        if (sentinel || !grid) return;

        sentinel = document.createElement('div');
        sentinel.className = 'jm-main-eje__scroll-sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        grid.insertAdjacentElement('afterend', sentinel);

        if (typeof IntersectionObserver !== 'undefined') {
          new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) renderNextBatch();
          }, { rootMargin: '600px 0px' }).observe(sentinel);
        }
        // Ademas, sondeo pasivo al hacer scroll: cubre navegadores sin
        // IntersectionObserver y los casos en que el observer no avisa.
        window.addEventListener('scroll', () => {
          if (sentinelWithinReach()) renderNextBatch();
        }, { passive: true });
      };

      // Si tras pintar un lote el centinela sigue a la vista (pantalla alta, o
      // lote más corto que el hueco), el observer no vuelve a dispararse por sí
      // solo, así que se encadena el siguiente.
      const continueIfStillVisible = () => {
        if (listState.done || listState.busy) return;
        if (sentinelWithinReach()) window.setTimeout(() => renderNextBatch(), 0);
      };

      async function renderNextBatch() {
        if (listState.busy || listState.done || !grid) return;

        const token = listState.token;
        const from = listState.rendered;
        const batch = listState.products.slice(from, from + productsPerPage());

        if (!batch.length) {
          listState.done = true;
          return;
        }

        listState.busy = true;
        setLoading(true);

        try {
          const cards = await cardsForProducts(batch);
          if (token !== listState.token) return;

          if (cards.length) {
            const fragment = document.createDocumentFragment();
            cards.forEach((item) => fragment.appendChild(item.card.cloneNode(true)));
            grid.appendChild(fragment);
          }

          listState.rendered = from + batch.length;
          listState.done = listState.rendered >= listState.products.length;
        } catch (error) {
          debugLog('renderNextBatch error', String(error));
          listState.done = true;
        } finally {
          if (token === listState.token) {
            listState.busy = false;
            setLoading(false);
          }
        }

        if (token === listState.token) continueIfStillVisible();
      }

      const startList = async (options = {}) => {
        if (!grid) return;

        const token = ++listState.token;
        listState.busy = false;
        listState.done = true;
        clearProducts();
        setLoading(true);

        try {
          // "Todas" de un eje junta los productos de todas sus colecciones (hasta
          // 70): esperar a todas tardaba varios segundos. En orden normal y sin
          // busqueda, se pintan ya las de las primeras colecciones y el resto se
          // suma por detras, en el mismo orden, sin tocar lo ya pintado.
          const progressive = !selectedRouteSelections().length && axisAllMode && activeAxis
            && activeAxis.collections.length > PROGRESSIVE_FIRST
            && (sortSelect?.value || 'manual') === 'manual' && !normalize(searchInput?.value || '');
          if (progressive) {
            // Con "sin stock al final" solo se adelantan los disponibles: la
            // union conserva el orden, asi que siguen siendo el principio de
            // la lista completa y lo ya pintado no se mueve.
            const firstAll = unionIndexes(await indexesFor(activeAxis.collections.slice(0, PROGRESSIVE_FIRST)));
            const first = soldOutLast ? firstAll.filter((item) => item.available) : firstAll;
            if (token !== listState.token) return;
            if (first.length) {
              listState.products = first;
              listState.rendered = 0;
              listState.done = false;
              ensureSentinel();
              if (empty) { empty.hidden = true; empty.textContent = ''; }
              await renderNextBatch();
              if (token !== listState.token) return;
            }
          }

          const products = await computeMatchingProducts();
          if (token !== listState.token) return;

          if (progressive && listState.rendered > 0) {
            listState.products = products;
            listState.done = listState.rendered >= products.length;
            continueIfStillVisible();
            return;
          }

          listState.products = products;
          listState.rendered = 0;

          if (!products.length) {
            showEmpty(noResultsMessage);
            return;
          }

          listState.done = false;
          ensureSentinel();
          if (empty) {
            empty.hidden = true;
            empty.textContent = '';
          }

          await renderNextBatch();
          if (token !== listState.token) return;

          // Hay productos que coinciden pero no se pudo pintar ni una tarjeta:
          // la vista ligera de colección no respondió como se espera. Se avisa
          // en vez de dejar la rejilla vacía y en silencio.
          if (!grid.children.length) {
            debugLog('sin tarjetas para los productos coincidentes', {
              matched: listState.products.length,
              sources: Array.from(new Set(listState.products.map((product) => product.sourceHandle)))
            });
            showEmpty(L.loadError);
            listState.done = true;
            return;
          }

          if (options.scroll) (get('.jm-main-eje__catalog') || section).scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (error) {
          if (token !== listState.token) return;
          debugLog('startList error', String(error));
          section.classList.remove('is-updating');
          showEmpty(L.loadError);
        } finally {
          if (token === listState.token) setLoading(false);
        }
      };

      const loadCollectionProducts = async (options = {}) => {
        if (!grid) return;
        ensureBaseAxis();
        syncActiveCollection();
        await startList(options);
      };

      const renderAll = (shouldLoadProducts = true) => {
        syncActiveCollection();
        setPalette();
        setHeader();
        renderAxisNav();
        renderCollections();
        renderTags();
        renderChildren();
        updateExplorerUrl();
        if (shouldLoadProducts) loadCollectionProducts();
      };

      const debounce = (callback, delay = 140) => {
        let timer;
        return (...args) => {
          window.clearTimeout(timer);
          timer = window.setTimeout(() => callback(...args), delay);
        };
      };

      collectionSearch?.addEventListener('input', debounce(() => {
        renderCollections();
      }));

      searchInput?.addEventListener('input', debounce(() => {
        loadCollectionProducts();
      }));

      sortSelect?.addEventListener('change', () => {
        loadCollectionProducts();
      });

      // Liquid solo entrega 50 colecciones por eje, y los ejes tienen hasta 72.
      // El Explorar llevaba desde siempre sin mostrar las que sobraban: de
      // Disenos faltaban 22, de Licencias 10 y de Tipo de Producto 2.
      //
      // El catalogo de /collections?view=data trae el resto. La pertenencia no
      // sale del metaobjeto --que es donde esta el tope-- sino de las etiquetas
      // de producto, igual que el resto del Explorar: cada eje publica una
      // sonda con una coleccion suya y las etiquetas de uno de sus productos, y
      // de ahi se deduce su prefijo. Todas las comparaciones van contra el
      // handle, nunca contra texto visible, para que no dependa del idioma.
      //
      // Es la misma peticion que usa el listado de colecciones, asi que si ya
      // se ha visitado sale de la cache del navegador.
      const handleizeTag = (value) => normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const splitTag = (tag) => {
        const parts = String(tag || '').split(' - ');
        if (parts.length < 2) return null;
        return { prefijo: handleizeTag(parts[0]), sufijo: handleizeTag(parts.slice(1).join(' - ')) };
      };

      const completeAxesFromCatalog = (catalog) => {
        const ejePorPrefijo = new Map();
        (catalog.ejes || []).forEach((eje) => {
          (eje.pt || []).forEach((tag) => {
            const t = splitTag(tag);
            if (t && t.sufijo === eje.ph) {
              ejePorPrefijo.set(t.prefijo, eje.h);
              // el modo inteligente usa los mismos prefijos
              axisByPrefix.set(t.prefijo, eje.h);
            }
          });
        });

        // Padres del catalogo: sus hijas (las que Liquid no entrego)
        Object.keys(catalog.cols || {}).forEach((handle) => {
          const col = catalog.cols[handle];
          if (!col.ch || childrenByParent.has(normalize(handle))) return;
          childrenByParent.set(normalize(handle), col.ch.map(([kid, title]) => ({ handle: kid, key: normalize(kid), title: title || kid })));
        });

        const childKeys = new Set();
        childrenByParent.forEach((kids) => kids.forEach((kid) => childKeys.add(kid.key)));

        const extrasPorEje = new Map();
        // Un padre no tiene productos: su eje es el de sus hijas
        const ejeDeHijas = (col) => {
          for (const [kid] of col.ch || []) {
            const kidCol = catalog.cols[kid];
            // la etiqueta de la propia hija (su valor es el handle de la hija)
            for (const tag of (kidCol && kidCol.tg) || []) {
              const t = splitTag(tag);
              if (t && t.sufijo === normalize(kid) && ejePorPrefijo.has(t.prefijo)) return ejePorPrefijo.get(t.prefijo);
            }
          }
          return null;
        };
        Object.keys(catalog.cols || {}).forEach((handle) => {
          const col = catalog.cols[handle];
          if (col.ch) {
            // padre de un eje = sus hijas no estan en ningun eje (un padre
            // promocional como "Noches de Verano" agrupa colecciones de eje)
            const allAxisKeys = new Set(axes.flatMap((axis) => axis.collectionKeys));
            if (col.ch.some(([kid]) => allAxisKeys.has(normalize(kid)))) return;
            const eje = ejeDeHijas(col);
            if (eje) {
              if (!extrasPorEje.has(eje)) extrasPorEje.set(eje, []);
              extrasPorEje.get(eje).push({ handle, title: col.t });
            }
            return;
          }
          // las hijas no van en el eje: salen como subfiltro de su padre
          if (!col.tg || childKeys.has(normalize(handle))) return;
          for (let i = 0; i < col.tg.length; i += 1) {
            const t = splitTag(col.tg[i]);
            if (!t || t.sufijo !== handle) continue;
            const eje = ejePorPrefijo.get(t.prefijo);
            if (eje) {
              if (!extrasPorEje.has(eje)) extrasPorEje.set(eje, []);
              extrasPorEje.get(eje).push({ handle, title: col.t });
            }
            return;
          }
        });

        // Liquid no dice el orden de estas en el eje; los ejes van en orden
        // alfabetico, asi que se anaden detras de las 50 en ese orden.
        const extrasCollator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });
        let added = 0;
        axes.forEach((axis) => {
          (extrasPorEje.get(axis.handle) || []).sort((a, b) => extrasCollator.compare(a.title, b.title)).forEach(({ handle, title }) => {
            const key = normalize(handle);
            if (axis.collectionKeys.indexOf(key) >= 0) return;
            axis.collections.push(handle);
            axis.collectionKeys.push(key);
            axis.collectionTitles.push(title);
            axis.collectionItems.push({ handle, key, title, search: normalize(`${handle} ${title}`) });
            added += 1;
          });
        });
        return added;
      };

      // El catalogo tarda ~2 s en generarse y no cambia de una pagina a otra:
      // se guarda 10 minutos en esta pestana del navegador (sessionStorage).
      // Si no se puede guardar (modo privado, cuota), se pide sin mas.
      const CATALOG_TTL = 10 * 60 * 1000;
      const cachedCatalog = async (url) => {
        const key = `cl-catalog:${url.pathname}${url.search}:${document.documentElement.lang || ''}`;
        try {
          const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
          if (saved && Date.now() - saved.time < CATALOG_TTL) return saved.data;
        } catch (error) { /* sin cache */ }
        const data = await fetchJson(url);
        try { sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), data })); } catch (error) { /* sin cache */ }
        return data;
      };

      // Los productos se cargan ya; las pestanas y la lista de colecciones
      // esperan (ocultas, ocupando su sitio) al catalogo, que trae los prefijos
      // de etiqueta de cada eje y los padres e hijas completos. Asi nada
      // aparece y luego desaparece, y los productos no esperan.
      if (config.catalogUrl) {
        section.classList.add('is-preparing');
        renderAll(true);
        cachedCatalog(new URL(config.catalogUrl, window.location.origin))
          .then((catalog) => {
            const added = completeAxesFromCatalog(catalog);
            dropBaseAxis();
            debugLog('ejes completados desde el catalogo', { added });
            // ?collection= de una coleccion que Liquid no entrego (mas alla de
            // las 50 primeras del eje): se elige ahora y se recargan productos
            if (requestedCollection && !selectedAxisList().length) {
              const key = normalize(requestedCollection);
              let axis = axes.find((item) => item.collectionKeys.includes(key));
              let handle = axis ? findCollectionForAxis(axis, requestedCollection) : '';
              if (!axis) {
                for (const item of axes) {
                  const parent = item.collections.find((h) => childrenOf(h).some((kid) => kid.key === key));
                  if (parent) { axis = item; handle = parent; selectedChild[normalize(parent)] = requestedCollection; break; }
                }
              }
              if (axis && handle) {
                activeAxis = axis;
                selectedByAxis[axis.handle] = handle;
                baseAxisHandle = axis.handle;
                axisAllMode = false;
                syncActiveCollection();
                setPalette();
                setHeader();
                updateExplorerUrl();
                loadCollectionProducts();
              }
            }
            // las pestanas salen con lo que de verdad es posible: se espera a la
            // primera lista (como mucho 2,5 s, para no dejarlas ocultas)
            return Promise.race([firstList, new Promise((resolve) => setTimeout(resolve, 2500))]);
          })
          .then(() => computeAvailability(lastAxisAllItems))
          .catch((error) => debugLog('no se pudo completar los ejes', String(error)))
          .finally(() => {
            renderAxisNav();
            renderCollections();
            renderTags();
            renderChildren();
            section.classList.remove('is-preparing');
          });
      } else {
        renderAll(true);
      }

  };

  const boot = () => {
    document.querySelectorAll('[data-section-type="main-eje"]').forEach(initEje);
  };

  boot();

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-section-type="main-eje"]').forEach(initEje);
  });
})();
